import React, { useEffect, useLayoutEffect, useState } from "react";
import { FONT, T, focusRing, prefersReducedMotion } from "./tokens";

// ===========================================================================
// CoachMarks — a 3-step tooltip tour shown once, on first visit to the editor.
// A localStorage flag (STORAGE_KEY) guarantees it never re-shows. Each step
// anchors to an element by id (targetId); if the target is missing it falls
// back to a centered card. Fully keyboard-driven (Enter/→ next, Esc skip).
// ===========================================================================

const STORAGE_KEY = "vella_showroom_coach_v1";

export function coachSeen() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return true; // no storage → don't nag
  }
}

const DEFAULT_STEPS = [
  { title: "This is your live site", body: "A real preview of what visitors see. Scroll around — it's the published page.", targetId: "showroom-frame" },
  { title: "Click any section to edit it", body: "Turn on Edit, then click a section. Tell Vella what to change and it applies to just that part.", targetId: "showroom-edit-toggle" },
  { title: "Publish when you're happy", body: "Edits stay in a private draft until you publish. You can always discard or roll back.", targetId: "showroom-publish-anchor" },
];

export default function CoachMarks({ steps = DEFAULT_STEPS, onDone }) {
  const [visible, setVisible] = useState(() => !coachSeen());
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  const finish = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setVisible(false);
    onDone && onDone();
  };

  const step = steps[i];

  useLayoutEffect(() => {
    if (!visible || !step) return;
    const measure = () => {
      const el = step.targetId && document.getElementById(step.targetId);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [visible, step]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      else if (e.key === "Enter" || e.key === "ArrowRight") {
        e.preventDefault();
        next();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, i]);

  if (!visible || !step) return null;

  const next = () => {
    if (i >= steps.length - 1) finish();
    else setI((n) => n + 1);
  };

  // Tooltip placement: prefer just below the target, but clamp fully on-screen.
  // Large targets (e.g. the full-height frame) would push the card off the
  // bottom, so we fall back to sitting just inside the target's top edge.
  const margin = 12;
  const CARD_H = 210;
  const CARD_W = 300;
  let cardStyle;
  if (rect) {
    const vh = window.innerHeight;
    let top = rect.bottom + margin;
    if (top + CARD_H > vh - 16) {
      // no room below — anchor near the target's top, clamped to the viewport
      top = Math.max(72, Math.min(rect.top + margin, vh - CARD_H - 16));
    }
    const left = Math.max(16, Math.min(rect.left, window.innerWidth - CARD_W - 16));
    cardStyle = { top, left };
  } else {
    cardStyle = { top: "50%", left: "50%", transform: "translate(-50%,-50%)" };
  }

  const reduce = prefersReducedMotion();

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000 }} role="dialog" aria-modal="true" aria-label="Editor tour">
      {/* Scrim with a spotlight cut-out over the target. */}
      <div
        onClick={next}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15,23,42,0.34)",
          animation: reduce ? "none" : "fadeIn 0.2s ease",
        }}
      />
      {rect && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            borderRadius: T.radiusMd,
            boxShadow: "0 0 0 9999px rgba(15,23,42,0.34)",
            outline: `2px solid ${T.blue}`,
            pointerEvents: "none",
            transition: reduce ? "none" : `all var(--ds-dur-base) ${T.easeOut}`,
          }}
        />
      )}

      <div
        style={{
          position: "absolute",
          width: 300,
          padding: 16,
          borderRadius: T.radiusMd,
          background: T.surface,
          border: `1px solid ${T.line}`,
          boxShadow: T.shadowLg,
          fontFamily: FONT,
          animation: reduce ? "none" : "slideUp 0.24s ease",
          ...cardStyle,
        }}
      >
        <div style={{ display: "flex", gap: 5, marginBottom: 12 }} aria-hidden="true">
          {steps.map((_, n) => (
            <span
              key={n}
              style={{
                height: 4,
                flex: 1,
                borderRadius: T.radiusFull,
                background: n <= i ? T.blue : T.line2,
                transition: reduce ? "none" : `background var(--ds-dur-base) ${T.easeOut}`,
              }}
            />
          ))}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginBottom: 6 }}>{step.title}</div>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: T.ink2, marginBottom: 16 }}>{step.body}</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button
            type="button"
            onClick={finish}
            style={{ border: "none", background: "transparent", color: T.ink3, fontFamily: FONT, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
          >
            Skip
          </button>
          <button
            type="button"
            onClick={next}
            autoFocus
            onFocus={(e) => (e.currentTarget.style.boxShadow = focusRing.boxShadow)}
            onBlur={(e) => (e.currentTarget.style.boxShadow = T.shadowSm)}
            style={{
              height: 32,
              padding: "0 18px",
              borderRadius: T.radiusFull,
              border: `1px solid ${T.blue}`,
              background: T.blue,
              color: "#fff",
              fontFamily: FONT,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: T.shadowSm,
            }}
          >
            {i >= steps.length - 1 ? "Got it" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}
